import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  GraduationCap, Plus, Loader2, Trash2, Users, Clock, Calendar,
  FileText, BookOpen, Pencil, Shield, CheckCircle2, PlayCircle,
  ClipboardList, Award, ChevronRight, X, FileSignature, Upload,
  FolderCheck, Mail,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { ListSkeleton } from "../components/Skeletons";
import { EnrollmentDossier } from "../components/lms/EnrollmentDossier";

const inputCls = "bg-[#0b0811] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]";
const TABS = [
  { key: "sessions", label: "Sessions", icon: Calendar },
  { key: "dossiers", label: "Dossiers Qualiopi", icon: FolderCheck },
  { key: "qualiopi", label: "Qualiopi", icon: Shield },
  { key: "stats", label: "Statistiques BPF", icon: Award },
];

const SEQUENCE_TYPES = [
  { v: "text", l: "Texte riche" },
  { v: "pdf", l: "PDF" },
  { v: "video", l: "Video (upload)" },
  { v: "youtube", l: "YouTube" },
  { v: "vimeo", l: "Vimeo" },
  { v: "quiz", l: "Quiz" },
  { v: "scorm", l: "SCORM (zip)" },
  { v: "external", l: "Lien externe" },
];

export default function FormationLMSPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();

  const [tab, setTab] = useState("sessions");
  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [variables, setVariables] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, t, dt, st] = await Promise.all([
        api.get("/lms/programs"),
        api.get("/lms/sessions"),
        api.get("/lms/qualiopi/templates"),
        api.get("/lms/qualiopi/doc-types"),
        api.get("/lms/stats/bpf"),
      ]);
      setPrograms(p.data || []);
      setSessions(s.data || []);
      setTemplates(t.data || []);
      setDocTypes(dt.data?.types || []);
      setVariables(dt.data?.variables || []);
      setStats(st.data);
    } catch (e) {
      toast.error("Erreur chargement LMS: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="formation-lms-page">
      <PageHeader
        icon={<GraduationCap />}
        title="Formation LMS & Qualiopi"
        description="Catalogue programmes, sessions, e-learning, documents Qualiopi"
        testId="formation-lms-header"
      />

      <div className="flex gap-2 mb-6 border-b border-white/10">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            data-testid={`lms-tab-${key}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors ${tab === key ? "text-[#f7c935] border-b-2 border-[#f7c935]" : "text-slate-400 hover:text-white"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {loading && <ListSkeleton items={5} testId="formation-lms-loading-skeleton" />}

      {!loading && tab === "sessions" && <SessionsTab sessions={sessions} programs={programs} templates={templates} reload={loadAll} api={api} />}
      {!loading && tab === "dossiers" && <DossiersTab api={api} />}
      {!loading && tab === "qualiopi" && <QualiopiTab templates={templates} docTypes={docTypes} variables={variables} reload={loadAll} api={api} />}
      {!loading && tab === "stats" && <StatsTab stats={stats} />}
    </div>
  );
}

// ─── Programs Tab ──────────────────────────────────────────
function ProgramsTab({ programs, reload, api }) {
  const [showCreate, setShowCreate] = useState(false);
  const [editProg, setEditProg] = useState(null);
  const [seqDialogProg, setSeqDialogProg] = useState(null);
  const emptyForm = { title: "", description: "", category: "", duration_hours: 7, duration_days: 1, max_participants: 10, price_ht: 0, modality: "presentiel", objectives: [], prerequisites: "", target_audience: "", evaluation_methods: [], certification: "" };
  const [form, setForm] = useState(emptyForm);

  const save = async () => {
    try {
      if (editProg) await api.put(`/lms/programs/${editProg.program_id}`, form);
      else await api.post("/lms/programs", form);
      toast.success(editProg ? "Programme mis a jour" : "Programme cree");
      setShowCreate(false); setEditProg(null); setForm(emptyForm);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce programme et ses sequences ?")) return;
    await api.delete(`/lms/programs/${id}`);
    toast.success("Programme supprime");
    reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setEditProg(null); setForm(emptyForm); setShowCreate(true); }} className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948]" data-testid="create-program-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau programme
        </Button>
      </div>
      {programs.length === 0 ? (
        <div className="text-center py-16 text-slate-500 border border-dashed border-white/10 rounded-xl">
          Aucun programme. Creez le premier pour demarrer votre LMS.
        </div>
      ) : (
        <div className="grid gap-3">
          {programs.map(p => (
            <div key={p.program_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 flex items-center justify-between" data-testid={`program-${p.program_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <BookOpen className="w-4 h-4 text-[#f7c935]" />
                  <span className="font-bold text-white">{p.title}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-slate-400">{p.modality}</span>
                </div>
                <div className="text-xs text-slate-400">{p.duration_hours}h · {p.duration_days}j · {p.max_participants} pax · {p.price_ht}€ HT</div>
              </div>
              <div className="flex gap-2 items-center">
                <Button size="sm" variant="ghost" onClick={() => setSeqDialogProg(p)} data-testid={`sequences-${p.program_id}`} className="text-slate-300 hover:text-[#f7c935]">
                  <PlayCircle className="w-4 h-4 mr-1" /> Sequences
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditProg(p); setForm({ ...emptyForm, ...p }); setShowCreate(true); }} className="text-slate-300"><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(p.program_id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) { setShowCreate(false); setEditProg(null); } }}>
        <DialogContent className="max-w-2xl bg-[#0b0811] border border-white/10 text-white">
          <DialogHeader><DialogTitle>{editProg ? "Modifier programme" : "Nouveau programme"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
            <Input placeholder="Titre" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="program-title" />
            <Textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className={inputCls} />
            <div className="grid grid-cols-3 gap-2">
              <Input type="number" placeholder="Heures" value={form.duration_hours} onChange={e => setForm({ ...form, duration_hours: Number(e.target.value) })} className={inputCls} />
              <Input type="number" placeholder="Jours" value={form.duration_days} onChange={e => setForm({ ...form, duration_days: Number(e.target.value) })} className={inputCls} />
              <Input type="number" placeholder="Max pax" value={form.max_participants} onChange={e => setForm({ ...form, max_participants: Number(e.target.value) })} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" placeholder="Prix HT" value={form.price_ht} onChange={e => setForm({ ...form, price_ht: Number(e.target.value) })} className={inputCls} />
              <select value={form.modality} onChange={e => setForm({ ...form, modality: e.target.value })} className={`${inputCls} px-3 py-2 rounded`}>
                <option value="presentiel">Presentiel</option>
                <option value="distanciel">Distanciel</option>
                <option value="mixte">Mixte</option>
                <option value="elearning">E-learning</option>
              </select>
            </div>
            <Textarea placeholder="Objectifs (un par ligne)" value={(form.objectives || []).join("\n")} onChange={e => setForm({ ...form, objectives: e.target.value.split("\n").filter(Boolean) })} rows={3} className={inputCls} />
            <Textarea placeholder="Prerequis" value={form.prerequisites} onChange={e => setForm({ ...form, prerequisites: e.target.value })} rows={2} className={inputCls} />
            <Textarea placeholder="Public cible" value={form.target_audience} onChange={e => setForm({ ...form, target_audience: e.target.value })} rows={2} className={inputCls} />
            <Input placeholder="Certification (RNCP, etc.)" value={form.certification} onChange={e => setForm({ ...form, certification: e.target.value })} className={inputCls} />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={save} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="save-program-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>

      <SequencesDialog program={seqDialogProg} onClose={() => setSeqDialogProg(null)} api={api} />
    </div>
  );
}

// ─── Sequences Dialog ──────────────────────────────────────
function SequencesDialog({ program, onClose, api }) {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editSeq, setEditSeq] = useState(null);
  const emptyForm = { title: "", type: "text", order: 0, duration_min: 15, content_text: "", external_url: "", quiz_questions: [], quiz_pass_score: 70 };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    if (!program) return;
    setLoading(true);
    try {
      const r = await api.get(`/lms/programs/${program.program_id}`);
      setSequences(r.data?.sequences || []);
    } finally { setLoading(false); }
  // eslint-disable-next-line
  }, [program?.program_id]);

  useEffect(() => { if (program) load(); /* eslint-disable-next-line */ }, [program]);

  const save = async () => {
    try {
      if (editSeq) await api.put(`/lms/sequences/${editSeq.sequence_id}`, form);
      else await api.post(`/lms/programs/${program.program_id}/sequences`, form);
      toast.success("OK");
      setShowAdd(false); setEditSeq(null); setForm(emptyForm);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette sequence ?")) return;
    await api.delete(`/lms/sequences/${id}`);
    load();
  };

  const addQuizQuestion = () => {
    setForm({ ...form, quiz_questions: [...(form.quiz_questions || []), { question: "", choices: ["", ""], correct_index: 0, points: 1 }] });
  };

  if (!program) return null;

  return (
    <Dialog open={!!program} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl bg-[#0b0811] border border-white/10 text-white">
        <DialogHeader><DialogTitle>Sequences — {program.title}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
          {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-6 text-[#f7c935]" /> :
            sequences.length === 0 ? <div className="text-center py-8 text-slate-500">Aucune sequence</div> :
              sequences.map(s => (
                <div key={s.sequence_id} className="bg-[#14101c] border border-white/10 rounded-lg p-3 flex items-center justify-between" data-testid={`seq-${s.sequence_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-[#f7c935]/10 text-[#f7c935] px-2 py-0.5 rounded">#{s.order}</span>
                      <span className="font-semibold">{s.title}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-slate-400">{s.type}</span>
                    </div>
                    <div className="text-xs text-slate-500">{s.duration_min} min</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditSeq(s); setForm({ ...emptyForm, ...s }); setShowAdd(true); }}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(s.sequence_id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
        </div>
        <div className="flex justify-end mt-3">
          <Button onClick={() => { setEditSeq(null); setForm(emptyForm); setShowAdd(true); }} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="add-seq-btn"><Plus className="w-4 h-4 mr-2" />Sequence</Button>
        </div>

        <Dialog open={showAdd} onOpenChange={(o) => { if (!o) { setShowAdd(false); setEditSeq(null); } }}>
          <DialogContent className="max-w-2xl bg-[#0b0811] border border-white/10 text-white">
            <DialogHeader><DialogTitle>{editSeq ? "Modifier" : "Nouvelle"} sequence</DialogTitle></DialogHeader>
            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-2">
              <Input placeholder="Titre" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="seq-title" />
              <div className="grid grid-cols-3 gap-2">
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className={`${inputCls} px-3 py-2 rounded`}>
                  {SEQUENCE_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
                <Input type="number" placeholder="Ordre" value={form.order || 0} onChange={e => setForm({ ...form, order: Number(e.target.value) })} className={inputCls} />
                <Input type="number" placeholder="Min" value={form.duration_min} onChange={e => setForm({ ...form, duration_min: Number(e.target.value) })} className={inputCls} />
              </div>
              {form.type === "text" && (
                <Textarea placeholder="Contenu (markdown supporte)" value={form.content_text || ""} onChange={e => setForm({ ...form, content_text: e.target.value })} rows={6} className={inputCls} />
              )}
              {(form.type === "youtube" || form.type === "vimeo" || form.type === "external") && (
                <Input placeholder={`URL ${form.type}`} value={form.external_url || ""} onChange={e => setForm({ ...form, external_url: e.target.value })} className={inputCls} />
              )}
              {(form.type === "pdf" || form.type === "video") && editSeq && (
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer bg-white/5 border border-white/10 rounded px-3 py-2 text-xs hover:bg-white/10 inline-flex items-center">
                    <Upload className="w-4 h-4 mr-2" /> Upload fichier
                    <input type="file" className="hidden" onChange={async e => {
                      if (!e.target.files?.[0]) return;
                      const fd = new FormData(); fd.append("file", e.target.files[0]);
                      try {
                        await api.post(`/lms/sequences/${editSeq.sequence_id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                        toast.success("Fichier uploade"); load();
                      } catch (ex) { toast.error(ex.response?.data?.detail || ex.message); }
                    }} />
                  </label>
                  {editSeq.content_filename && <span className="text-xs text-slate-400">{editSeq.content_filename}</span>}
                </div>
              )}
              {form.type === "scorm" && !editSeq && (
                <div className="text-xs text-slate-400 bg-white/5 border border-white/10 rounded p-3">Enregistrez d'abord la séquence, puis rouvrez-la pour importer le package SCORM (.zip).</div>
              )}
              {form.type === "scorm" && editSeq && <ScormUploadBlock seq={editSeq} api={api} onDone={load} />}
              {form.type === "quiz" && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Questions du quiz (score requis: {form.quiz_pass_score}%)</span>
                    <Input type="number" value={form.quiz_pass_score || 70} onChange={e => setForm({ ...form, quiz_pass_score: Number(e.target.value) })} className={`${inputCls} w-20 text-xs`} />
                  </div>
                  {(form.quiz_questions || []).map((q, i) => (
                    <div key={i} className="bg-[#14101c] border border-white/10 rounded p-2 space-y-1">
                      <Input placeholder={`Question ${i + 1}`} value={q.question} onChange={e => {
                        const nq = [...form.quiz_questions]; nq[i] = { ...q, question: e.target.value };
                        setForm({ ...form, quiz_questions: nq });
                      }} className={inputCls} />
                      {(q.choices || []).map((c, ci) => (
                        <div key={ci} className="flex items-center gap-2">
                          <input type="radio" name={`q${i}`} checked={q.correct_index === ci} onChange={() => {
                            const nq = [...form.quiz_questions]; nq[i] = { ...q, correct_index: ci };
                            setForm({ ...form, quiz_questions: nq });
                          }} />
                          <Input placeholder={`Reponse ${ci + 1}`} value={c} onChange={e => {
                            const nq = [...form.quiz_questions]; const nc = [...q.choices]; nc[ci] = e.target.value;
                            nq[i] = { ...q, choices: nc };
                            setForm({ ...form, quiz_questions: nq });
                          }} className={inputCls} />
                          <Button size="sm" variant="ghost" onClick={() => {
                            const nq = [...form.quiz_questions]; const nc = q.choices.filter((_, x) => x !== ci);
                            nq[i] = { ...q, choices: nc, correct_index: Math.min(q.correct_index, nc.length - 1) };
                            setForm({ ...form, quiz_questions: nq });
                          }} className="text-red-400"><X className="w-3 h-3" /></Button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => {
                          const nq = [...form.quiz_questions]; nq[i] = { ...q, choices: [...q.choices, ""] };
                          setForm({ ...form, quiz_questions: nq });
                        }} className="text-xs">+ reponse</Button>
                        <Button size="sm" variant="ghost" onClick={() => {
                          setForm({ ...form, quiz_questions: form.quiz_questions.filter((_, x) => x !== i) });
                        }} className="text-xs text-red-400">Suppr question</Button>
                      </div>
                    </div>
                  ))}
                  <Button size="sm" onClick={addQuizQuestion} className="bg-white/5 hover:bg-white/10 text-xs">+ Question</Button>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="ghost" onClick={() => { setShowAdd(false); setEditSeq(null); }}>Annuler</Button>
              <Button onClick={save} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="save-seq-btn">Enregistrer</Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

// ─── Sessions Tab ──────────────────────────────────────────
function SessionsTab({ sessions, programs, templates, reload, api }) {
  const [showCreate, setShowCreate] = useState(false);
  const [editSess, setEditSess] = useState(null);
  const [detailSess, setDetailSess] = useState(null);
  const emptyForm = { program_id: "", title: "", start_date: "", end_date: "", location: "", modality: "presentiel", trainer_name: "", trainer_email: "", max_participants: 10, price_ht: 0, notes: "", financement: "", opco: "" };
  const [form, setForm] = useState(emptyForm);

  const save = async () => {
    try {
      if (editSess) await api.put(`/lms/sessions/${editSess.session_id}`, form);
      else await api.post("/lms/sessions", form);
      toast.success("OK");
      setShowCreate(false); setEditSess(null); setForm(emptyForm);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette session ?")) return;
    await api.delete(`/lms/sessions/${id}`);
    reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setEditSess(null); setForm(emptyForm); setShowCreate(true); }} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="create-session-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle session
        </Button>
      </div>
      {sessions.length === 0 ? (
        <div className="text-center py-16 text-slate-500 border border-dashed border-white/10 rounded-xl">Aucune session programmee</div>
      ) : (
        <div className="grid gap-3">
          {sessions.map(s => (
            <div key={s.session_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5" data-testid={`session-${s.session_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-[#f7c935]" />
                    <span className="font-bold">{s.title || s.program_title}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-[#f7c935]/10 text-[#f7c935]">{s.status}</span>
                  </div>
                  <div className="text-xs text-slate-400">{s.start_date} → {s.end_date} · {s.location || "—"} · {s.modality} · Max {s.max_participants}</div>
                  {s.trainer_name && <div className="text-xs text-slate-500 mt-1">Formateur: {s.trainer_name}</div>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setDetailSess(s)} className="text-[#f7c935]"><ChevronRight className="w-4 h-4" /> Gerer</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditSess(s); setForm({ ...emptyForm, ...s }); setShowCreate(true); }}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(s.session_id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) { setShowCreate(false); setEditSess(null); } }}>
        <DialogContent className="max-w-2xl bg-[#0b0811] border border-white/10 text-white">
          <DialogHeader><DialogTitle>{editSess ? "Modifier session" : "Nouvelle session"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
            <select value={form.program_id} onChange={e => setForm({ ...form, program_id: e.target.value })} className={`${inputCls} px-3 py-2 rounded w-full`} data-testid="session-program">
              <option value="">— Programme —</option>
              {programs.map(p => <option key={p.program_id} value={p.program_id}>{p.title}</option>)}
            </select>
            <Input placeholder="Titre (optionnel)" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className={inputCls} />
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className={inputCls} data-testid="session-start" />
              <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className={inputCls} data-testid="session-end" />
            </div>
            <Input placeholder="Lieu" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className={inputCls} />
            <div className="grid grid-cols-2 gap-2">
              <select value={form.modality} onChange={e => setForm({ ...form, modality: e.target.value })} className={`${inputCls} px-3 py-2 rounded`}>
                <option value="presentiel">Presentiel</option>
                <option value="distanciel">Distanciel</option>
                <option value="mixte">Mixte</option>
                <option value="elearning">E-learning</option>
              </select>
              <Input type="number" placeholder="Max pax" value={form.max_participants} onChange={e => setForm({ ...form, max_participants: Number(e.target.value) })} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Formateur (nom)" value={form.trainer_name} onChange={e => setForm({ ...form, trainer_name: e.target.value })} className={inputCls} />
              <Input placeholder="Formateur email" value={form.trainer_email} onChange={e => setForm({ ...form, trainer_email: e.target.value })} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" placeholder="Prix HT" value={form.price_ht} onChange={e => setForm({ ...form, price_ht: Number(e.target.value) })} className={inputCls} />
              <Input placeholder="Financement (CPF / OPCO / ...)" value={form.financement} onChange={e => setForm({ ...form, financement: e.target.value })} className={inputCls} />
            </div>
            <Input placeholder="OPCO (si applicable)" value={form.opco} onChange={e => setForm({ ...form, opco: e.target.value })} className={inputCls} />
            <Textarea placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className={inputCls} />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={save} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="save-session-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>

      <SessionDetailDialog session={detailSess} onClose={() => setDetailSess(null)} api={api} templates={templates} />
    </div>
  );
}

// ─── Session Detail (enrollments + qualiopi docs) ──────────
function SessionDetailDialog({ session, onClose, api, templates }) {
  const [detail, setDetail] = useState(null);
  const [showEnroll, setShowEnroll] = useState(false);
  const [enrollForm, setEnrollForm] = useState({ student_name: "", student_email: "", student_phone: "", student_company: "" });
  const [genTemplate, setGenTemplate] = useState("");
  const [showLearnerSpace, setShowLearnerSpace] = useState(false);
  const [genBulkLoading, setGenBulkLoading] = useState(false);
  const [genBulkPlan, setGenBulkPlan] = useState(null);
  const [showGenBulk, setShowGenBulk] = useState(false);
  const [dossierEnrId, setDossierEnrId] = useState(null);
  const [dossierPcts, setDossierPcts] = useState({});

  const load = useCallback(async () => {
    if (!session) return;
    const r = await api.get(`/lms/sessions/${session.session_id}`);
    setDetail(r.data);
    try {
      const o = await api.get(`/lms/dossiers/overview?session_id=${session.session_id}`);
      const m = {};
      (o.data?.dossiers || []).forEach(d => { m[d.enrollment_id] = d.completion_pct; });
      setDossierPcts(m);
    } catch { /* overview optional */ }
  // eslint-disable-next-line
  }, [session?.session_id]);

  useEffect(() => { if (session) load(); /* eslint-disable-next-line */ }, [session]);

  if (!session) return null;

  const enroll = async () => {
    try {
      await api.post("/lms/enrollments", { session_id: session.session_id, ...enrollForm });
      toast.success("Stagiaire inscrit");
      setShowEnroll(false); setEnrollForm({ student_name: "", student_email: "", student_phone: "", student_company: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const delEnroll = async (id) => {
    if (!window.confirm("Retirer ce stagiaire ?")) return;
    await api.delete(`/lms/enrollments/${id}`);
    load();
  };

  const generate = async (enrollmentId) => {
    if (!genTemplate) { toast.error("Selectionnez un template"); return; }
    try {
      await api.post("/lms/qualiopi/generate", { session_id: session.session_id, template_id: genTemplate, enrollment_id: enrollmentId });
      toast.success("Document genere");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const openBulkGenerator = async () => {
    try {
      const r = await api.get(`/lms/sessions/${session.session_id}/qualiopi-plan`);
      setGenBulkPlan(r.data);
      setShowGenBulk(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const runBulkGenerate = async (replaceExisting) => {
    setGenBulkLoading(true);
    try {
      const r = await api.post(`/lms/sessions/${session.session_id}/generate-qualiopi-docs`, { replace_existing: replaceExisting });
      toast.success(`${r.data.generated_count} document(s) genere(s)` + (r.data.failed_count > 0 ? `, ${r.data.failed_count} en echec` : ""));
      setShowGenBulk(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setGenBulkLoading(false);
  };

  return (
    <Dialog open={!!session} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl bg-[#0b0811] border border-white/10 text-white">
        <DialogHeader><DialogTitle>{session.title || session.program_title} — {session.start_date}</DialogTitle></DialogHeader>
        <div className="flex gap-2 mb-2 flex-wrap">
          <Button size="sm" onClick={() => setShowLearnerSpace(true)} className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 text-xs" data-testid="configure-learner-space-btn">
            <ClipboardList className="w-3 h-3 mr-1" /> Configurer Espace Apprenant
          </Button>
          <Button size="sm" onClick={openBulkGenerator} className="bg-[#f7c935] text-[#14101c] font-bold text-xs hover:bg-[#ffd948]" data-testid="bulk-qualiopi-btn">
            <FileText className="w-3 h-3 mr-1" /> Generer docs Qualiopi (auto)
          </Button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-4">
          {/* Enrollments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold flex items-center gap-2"><Users className="w-4 h-4" /> Stagiaires ({detail?.enrollments?.length || 0})</h3>
              <Button size="sm" onClick={() => setShowEnroll(true)} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="add-enrollment-btn"><Plus className="w-4 h-4 mr-1" />Inscrire</Button>
            </div>
            {(detail?.enrollments || []).length === 0 ? <div className="text-slate-500 text-sm">Aucun stagiaire</div> :
              <div className="space-y-2">
                {detail.enrollments.map(e => (
                  <div key={e.enrollment_id} className="bg-[#14101c] border border-white/10 rounded p-3" data-testid={`enr-${e.enrollment_id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold">{e.student_name} <span className="text-xs text-slate-500">{e.student_email}</span></div>
                        <div className="text-xs text-slate-400 flex items-center gap-2">
                          <span>{e.student_company || "—"} · Progression: {e.progress}%  · {e.status}</span>
                          {dossierPcts[e.enrollment_id] !== undefined && (
                            <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${dossierPcts[e.enrollment_id] >= 100 ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"}`} data-testid={`dossier-pct-${e.enrollment_id}`}>
                              Dossier {dossierPcts[e.enrollment_id]}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 items-center">
                        <Button size="sm" onClick={() => setDossierEnrId(e.enrollment_id)} className="bg-[#f7c935]/10 text-[#f7c935] hover:bg-[#f7c935]/20 text-xs h-7" data-testid={`open-dossier-${e.enrollment_id}`}>
                          <FolderCheck className="w-3 h-3 mr-1" /> Dossier
                        </Button>
                        <select value={genTemplate} onChange={e2 => setGenTemplate(e2.target.value)} className={`${inputCls} px-2 py-1 text-xs rounded`}>
                          <option value="">Doc Qualiopi...</option>
                          {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
                        </select>
                        <Button size="sm" variant="ghost" onClick={() => generate(e.enrollment_id)} className="text-[#f7c935]" data-testid={`gen-doc-${e.enrollment_id}`}><FileText className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => delEnroll(e.enrollment_id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>}
          </div>

          {/* Qualiopi docs */}
          <div>
            <h3 className="font-bold flex items-center gap-2 mb-2"><Shield className="w-4 h-4" /> Documents Qualiopi ({detail?.qualiopi_documents?.length || 0})</h3>
            {(detail?.qualiopi_documents || []).length === 0 ? <div className="text-slate-500 text-sm">Aucun document genere</div> :
              <div className="space-y-2">
                {detail.qualiopi_documents.map(d => (
                  <div key={d.document_id} className="bg-[#14101c] border border-white/10 rounded p-3 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#f7c935]" />
                        <span className="font-semibold">{d.doc_name}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-white/5">{d.doc_type}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded ${d.status === "signed" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>{d.status}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{d.enrollment_id ? `Pour un stagiaire` : "Document session"} · {Object.keys(d.signatures || {}).length} signature(s)</div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={async () => {
                      if (!window.confirm("Supprimer ?")) return;
                      await api.delete(`/lms/qualiopi/documents/${d.document_id}`);
                      load();
                    }} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>}
          </div>
        </div>

        <Dialog open={showEnroll} onOpenChange={(o) => { if (!o) setShowEnroll(false); }}>
          <DialogContent className="max-w-md bg-[#0b0811] border border-white/10 text-white">
            <DialogHeader><DialogTitle>Inscrire un stagiaire</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <Input placeholder="Nom complet" value={enrollForm.student_name} onChange={e => setEnrollForm({ ...enrollForm, student_name: e.target.value })} className={inputCls} data-testid="enr-name" />
              <Input placeholder="Email" value={enrollForm.student_email} onChange={e => setEnrollForm({ ...enrollForm, student_email: e.target.value })} className={inputCls} data-testid="enr-email" />
              <Input placeholder="Telephone" value={enrollForm.student_phone} onChange={e => setEnrollForm({ ...enrollForm, student_phone: e.target.value })} className={inputCls} />
              <Input placeholder="Entreprise" value={enrollForm.student_company} onChange={e => setEnrollForm({ ...enrollForm, student_company: e.target.value })} className={inputCls} />
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="ghost" onClick={() => setShowEnroll(false)}>Annuler</Button>
              <Button onClick={enroll} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="save-enrollment-btn">Inscrire</Button>
            </div>
          </DialogContent>
        </Dialog>
        {showLearnerSpace && <LearnerSpaceConfigDialog sessionId={session.session_id} api={api} onClose={() => setShowLearnerSpace(false)} />}
        {dossierEnrId && <EnrollmentDossier enrollmentId={dossierEnrId} api={api} onClose={() => setDossierEnrId(null)} onChanged={load} />}
        {showGenBulk && genBulkPlan && (
          <Dialog open onOpenChange={(o) => { if (!o) setShowGenBulk(false); }}>
            <DialogContent className="max-w-2xl bg-[#0b0811] border border-white/10 text-white" data-testid="bulk-qualiopi-dialog">
              <DialogHeader><DialogTitle>Generer automatiquement les documents Qualiopi</DialogTitle></DialogHeader>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                <div className="bg-[#14101c] border border-white/10 rounded-lg p-3 text-xs">
                  <div className="font-bold text-white mb-1">{genBulkPlan.session_title || session.title}</div>
                  <div className="text-slate-400">Programme: {genBulkPlan.program_title}</div>
                  <div className="text-slate-400 mt-1">Total a generer: <span className="text-[#f7c935] font-bold">{genBulkPlan.pending_docs_count}</span> PDF ({genBulkPlan.enrollments.length} stagiaire{genBulkPlan.enrollments.length > 1 ? "s" : ""} × {genBulkPlan.templates.length} template{genBulkPlan.templates.length > 1 ? "s" : ""})</div>
                  {genBulkPlan.existing_docs_count > 0 && <div className="text-amber-400 mt-1">{genBulkPlan.existing_docs_count} document(s) deja present(s) pour cette session</div>}
                </div>
                {genBulkPlan.templates.length === 0 ? (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300" data-testid="bulk-no-templates">
                    Aucun template Qualiopi rattache au programme. Allez dans la Bibliotheque &gt; Editer le programme &gt; Rattachements pour en ajouter.
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 font-bold mb-2">Documents a generer</div>
                      <div className="grid grid-cols-2 gap-2">
                        {genBulkPlan.templates.map(t => (
                          <div key={t.template_id} className="bg-[#14101c] border border-white/5 rounded px-3 py-2 text-xs">
                            <div className="font-semibold text-white">{t.title}</div>
                            <div className="text-slate-500">{t.doc_type}{t.requires_signature ? " · signature" : ""}{(t.linked_indicators || []).length > 0 ? ` · Ind ${(t.linked_indicators || []).join(", ")}` : ""}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 font-bold mb-2">Stagiaires concernes</div>
                      <div className="flex flex-wrap gap-2">
                        {genBulkPlan.enrollments.map(e => (
                          <span key={e.enrollment_id} className="text-xs bg-white/5 border border-white/10 rounded px-2 py-1">{e.student_name}</span>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="ghost" onClick={() => setShowGenBulk(false)}>Annuler</Button>
                {genBulkPlan.existing_docs_count > 0 && genBulkPlan.templates.length > 0 && (
                  <Button variant="ghost" onClick={() => runBulkGenerate(true)} disabled={genBulkLoading} className="text-amber-300 border border-amber-500/30" data-testid="bulk-regen-btn">
                    Remplacer & regenerer
                  </Button>
                )}
                {genBulkPlan.templates.length > 0 && (
                  <Button onClick={() => runBulkGenerate(false)} disabled={genBulkLoading} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="bulk-gen-confirm">
                    {genBulkLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}Generer les {genBulkPlan.pending_docs_count} PDF
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Learner Space Config Dialog (Digiforma-style) ────────
function LearnerSpaceConfigDialog({ sessionId, api, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [coverBlob, setCoverBlob] = useState(null);

  useEffect(() => {
    api.get(`/lms/sessions/${sessionId}/learner-space`).then(r => {
      setCfg(r.data);
      if (r.data?.cover_image_url) {
        api.get(`/lms/sessions/${sessionId}/cover-image`).then(p => setCoverBlob(`data:${p.data.mime};base64,${p.data.data_b64}`)).catch(() => {});
      }
    }).catch(() => toast.error("Erreur chargement config"));
  // eslint-disable-next-line
  }, [sessionId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/lms/sessions/${sessionId}/learner-space`, cfg);
      toast.success("Configuration enregistree");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const uploadCover = async (file) => {
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/lms/sessions/${sessionId}/cover-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover uploadee");
      const p = await api.get(`/lms/sessions/${sessionId}/cover-image`);
      setCoverBlob(`data:${p.data.mime};base64,${p.data.data_b64}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  if (!cfg) return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg bg-[#0b0811] border border-white/10 text-white"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935] mx-auto my-8" /></DialogContent>
    </Dialog>
  );

  const toggles = [
    { k: "messaging_enabled", l: "Messagerie instantanee" },
    { k: "show_program", l: "Afficher le programme" },
    { k: "show_location", l: "Afficher le lieu" },
    { k: "show_map", l: "Afficher la carte" },
    { k: "show_pedagogical_followup", l: "Suivi pedagogique" },
    { k: "show_planning", l: "Afficher le planning" },
    { k: "show_learner_list", l: "Afficher la liste des apprenants" },
    { k: "show_internal_regulations", l: "Reglement interieur" },
    { k: "show_documents", l: "Documents partages" },
    { k: "show_elearning", l: "E-learning" },
  ];

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl bg-[#0b0811] border border-white/10 text-white" data-testid="learner-space-config">
        <DialogHeader><DialogTitle>Configuration Espace Apprenant</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-2">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Nom d'affichage</label>
            <Input value={cfg.display_name || ""} onChange={e => set("display_name", e.target.value)} placeholder="Nom visible par les apprenants" className={inputCls} data-testid="ls-display-name" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Image de couverture</label>
            <div className="relative h-32 rounded-lg overflow-hidden border border-white/10 bg-[#14101c]">
              {coverBlob && <img src={coverBlob} alt="cover" className="w-full h-full object-cover" />}
              <label className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center text-sm">
                Uploader une cover
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => e.target.files?.[0] && uploadCover(e.target.files[0])} data-testid="ls-cover-upload" />
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Description (visible par les apprenants)</label>
            <Textarea value={cfg.description || ""} onChange={e => set("description", e.target.value)} rows={4} className={inputCls} data-testid="ls-description" />
          </div>
          <div className="pt-3 border-t border-white/10">
            <h3 className="text-sm font-bold mb-2">Visibilite des sections dans l'Espace Apprenant</h3>
            <div className="grid grid-cols-2 gap-2">
              {toggles.map(t => (
                <label key={t.k} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white/5 rounded px-2 py-1" data-testid={`ls-toggle-${t.k}`}>
                  <input type="checkbox" checked={!!cfg[t.k]} onChange={e => set(t.k, e.target.checked)} />
                  <span>{t.l}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="ls-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Enregistrer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Qualiopi Tab (Templates) ──────────────────────────────
function QualiopiTab({ templates, docTypes, variables, reload, api }) {
  const [showCreate, setShowCreate] = useState(false);
  const [editTpl, setEditTpl] = useState(null);
  const emptyForm = { name: "", doc_type: "convention", content: "", required_signature: true, signed_by: ["stagiaire", "formateur"], audience: "enrollment" };
  const [form, setForm] = useState(emptyForm);

  const save = async () => {
    try {
      if (editTpl) await api.put(`/lms/qualiopi/templates/${editTpl.template_id}`, form);
      else await api.post("/lms/qualiopi/templates", form);
      toast.success("OK");
      setShowCreate(false); setEditTpl(null); setForm(emptyForm);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/lms/qualiopi/templates/${id}`);
    reload();
  };

  const uploadSource = async (templateId, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/lms/qualiopi/templates/${templateId}/upload-source`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Extrait ${r.data.extracted_length} caracteres, ${r.data.detected_variables.length} balises detectees`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-400">
          <span className="font-semibold text-slate-300">Balises disponibles:</span>{" "}
          {variables.map(v => <code key={v} className="bg-white/5 px-1.5 py-0.5 mx-0.5 rounded text-[10px]">{v}</code>)}
        </div>
        <Button onClick={() => { setEditTpl(null); setForm(emptyForm); setShowCreate(true); }} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau template
        </Button>
      </div>
      {templates.length === 0 ? (
        <div className="text-center py-16 text-slate-500 border border-dashed border-white/10 rounded-xl">Aucun template Qualiopi</div>
      ) : (
        <div className="grid gap-3">
          {templates.map(t => (
            <div key={t.template_id} className="bg-[#14101c] border border-white/10 rounded-xl p-4 flex items-center justify-between" data-testid={`tpl-${t.template_id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#f7c935]" />
                  <span className="font-bold">{t.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-white/5">{t.doc_type}</span>
                  {t.required_signature && <span className="text-[10px] px-2 py-0.5 rounded bg-[#f7c935]/10 text-[#f7c935]">Signature</span>}
                </div>
                <div className="text-xs text-slate-500 mt-1">Audience: {t.audience} · {t.content?.length || 0} caracteres</div>
                {t.detected_variables?.length > 0 && <div className="text-[10px] text-slate-500 mt-1">Balises detectees: {t.detected_variables.join(", ")}</div>}
              </div>
              <div className="flex gap-1 items-center">
                <label className="cursor-pointer text-[10px] text-slate-400 hover:text-[#f7c935] inline-flex items-center gap-1">
                  <Upload className="w-3 h-3" /> Source PDF/DOCX
                  <input type="file" accept=".pdf,.docx" className="hidden" onChange={e => e.target.files?.[0] && uploadSource(t.template_id, e.target.files[0])} />
                </label>
                <Button size="sm" variant="ghost" onClick={() => { setEditTpl(t); setForm({ ...emptyForm, ...t }); setShowCreate(true); }}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(t.template_id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) { setShowCreate(false); setEditTpl(null); } }}>
        <DialogContent className="max-w-3xl bg-[#0b0811] border border-white/10 text-white">
          <DialogHeader><DialogTitle>{editTpl ? "Modifier template" : "Nouveau template Qualiopi"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
            <Input placeholder="Nom" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="tpl-name" />
            <div className="grid grid-cols-2 gap-2">
              <select value={form.doc_type} onChange={e => setForm({ ...form, doc_type: e.target.value })} className={`${inputCls} px-3 py-2 rounded`} data-testid="tpl-doc-type">
                {docTypes.map(dt => <option key={dt.key} value={dt.key}>{dt.label}</option>)}
              </select>
              <select value={form.audience} onChange={e => setForm({ ...form, audience: e.target.value })} className={`${inputCls} px-3 py-2 rounded`}>
                <option value="enrollment">1 document par stagiaire</option>
                <option value="session">1 document pour la session</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-400 flex items-center gap-2">
                <input type="checkbox" checked={!!form.required_signature} onChange={e => setForm({ ...form, required_signature: e.target.checked })} />
                Signature requise
              </label>
              {form.required_signature && (
                <div className="flex gap-2 text-xs">
                  {["stagiaire", "formateur", "organisme"].map(r => (
                    <label key={r} className="flex items-center gap-1">
                      <input type="checkbox" checked={(form.signed_by || []).includes(r)} onChange={e => {
                        const arr = new Set(form.signed_by || []);
                        if (e.target.checked) arr.add(r); else arr.delete(r);
                        setForm({ ...form, signed_by: [...arr] });
                      }} /> {r}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <Textarea placeholder="Contenu du template avec balises {{nom_stagiaire}}, {{date_debut}}, etc." value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={15} className={`${inputCls} font-mono text-xs`} data-testid="tpl-content" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={save} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="save-template-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Stats Tab ─────────────────────────────────────────────
function StatsTab({ stats }) {
  if (!stats) return <div className="text-slate-400">Aucune statistique</div>;
  const cards = [
    { label: "Sessions totales", value: stats.total_sessions, icon: Calendar },
    { label: "Stagiaires", value: stats.total_participants, icon: Users },
    { label: "Heures totales", value: stats.total_hours, icon: Clock },
    { label: "Termines", value: stats.completed_participants, icon: CheckCircle2 },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(c => (
          <div key={c.label} className="bg-[#14101c] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 text-slate-400 text-xs"><c.icon className="w-4 h-4" />{c.label}</div>
            <div className="text-3xl font-black mt-1">{c.value || 0}</div>
          </div>
        ))}
      </div>
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-5">
        <h3 className="font-bold mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4" />Sessions par modalite</h3>
        <div className="grid grid-cols-4 gap-3 text-sm">
          {Object.entries(stats.sessions_by_modality || {}).map(([k, v]) => (
            <div key={k} className="bg-[#0b0811] rounded p-3">
              <div className="text-slate-400 text-xs capitalize">{k}</div>
              <div className="text-2xl font-bold">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SCORM Upload (Iter 317) ────────────────────────────────
function ScormUploadBlock({ seq, api, onDone }) {
  const [prog, setProg] = useState(null);
  const [info, setInfo] = useState(seq.scorm_entry ? { scorm_version: seq.scorm_version, scorm_entry: seq.scorm_entry, filename: seq.content_filename, file_count: seq.scorm_file_count } : null);

  return (
    <div className="space-y-2 bg-[#14101c] border border-white/10 rounded p-3" data-testid="scorm-upload-block">
      <div className="text-xs text-slate-400">Package SCORM (.zip) — SCORM 1.2 ou 2004. Le module sera joué directement dans le portail élève avec suivi de progression, score et complétion.</div>
      <label className="cursor-pointer bg-white/5 border border-white/10 rounded px-3 py-2 text-xs hover:bg-white/10 inline-flex items-center" data-testid="scorm-upload-label">
        {prog !== null ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        {prog !== null ? `Import en cours... ${prog}%` : "Importer le package SCORM"}
        <input type="file" accept=".zip,application/zip" className="hidden" data-testid="scorm-upload-input" onChange={async e => {
          const f = e.target.files?.[0]; if (!f) return;
          const fd = new FormData(); fd.append("file", f);
          setProg(0);
          try {
            const r = await api.post(`/lms/sequences/${seq.sequence_id}/scorm/upload`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: ev => setProg(Math.round((ev.loaded / (ev.total || 1)) * 100)),
            });
            setInfo({ scorm_version: r.data.scorm_version, scorm_entry: r.data.scorm_entry, filename: r.data.filename, file_count: r.data.file_count });
            toast.success(`Package SCORM ${r.data.scorm_version} importé (${r.data.file_count} fichiers)`);
            onDone();
          } catch (ex) { toast.error(ex.response?.data?.detail || ex.message); }
          setProg(null);
        }} />
      </label>
      {info && (
        <div className="text-xs text-green-400 flex items-center gap-2 flex-wrap" data-testid="scorm-info">
          <CheckCircle2 className="w-3 h-3 shrink-0" />
          <span>{info.filename} — SCORM {info.scorm_version} · {info.file_count} fichiers · lancement : {info.scorm_entry}</span>
        </div>
      )}
    </div>
  );
}

// ─── Dossiers Qualiopi Tab (conformité par stagiaire, Iter 317) ─────
function DossiersTab({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | incomplete | complete
  const [dossierEnrId, setDossierEnrId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/lms/dossiers/overview");
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) return <ListSkeleton items={4} testId="dossiers-loading" />;
  const stats = data?.stats || { total: 0, complete: 0, incomplete: 0, avg_pct: 0 };
  const rows = (data?.dossiers || []).filter(d =>
    filter === "all" ? true : filter === "complete" ? d.completion_pct >= 100 : d.completion_pct < 100
  );

  return (
    <div className="space-y-4" data-testid="dossiers-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Dossiers", value: stats.total, cls: "text-white", key: "total" },
          { label: "Conformes", value: stats.complete, cls: "text-green-400", key: "complete" },
          { label: "Incomplets", value: stats.incomplete, cls: "text-amber-400", key: "incomplete" },
          { label: "Complétude moyenne", value: `${stats.avg_pct}%`, cls: "text-[#f7c935]", key: "avg" },
        ].map(k => (
          <div key={k.key} className="bg-[#14101c] border border-white/10 rounded-xl p-4" data-testid={`dossiers-kpi-${k.key}`}>
            <div className={`text-2xl font-bold ${k.cls}`}>{k.value}</div>
            <div className="text-xs text-slate-500">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        {[["all", "Tous"], ["incomplete", "Incomplets"], ["complete", "Conformes"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            data-testid={`dossiers-filter-${v}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${filter === v ? "bg-[#f7c935] text-[#14101c] border-[#f7c935] font-bold" : "border-white/10 text-slate-400 hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-16 text-slate-500 border border-dashed border-white/10 rounded-xl">
          Aucun dossier — inscrivez des stagiaires sur une session pour démarrer le suivi Qualiopi.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map(d => (
            <button key={d.enrollment_id} onClick={() => setDossierEnrId(d.enrollment_id)}
              data-testid={`dossier-row-${d.enrollment_id}`}
              className="w-full text-left bg-[#14101c] border border-white/10 rounded-xl p-4 hover:border-[#f7c935]/40 transition-colors">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{d.student_name}</span>
                    <span className="text-xs text-slate-500">{d.student_email}</span>
                    {d.completion_pct >= 100 ? (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/15 text-green-400 font-bold">Conforme</span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 font-bold">Incomplet</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{d.session_title} · {d.start_date}</div>
                  {d.missing_steps.length > 0 && (
                    <div className="text-[10px] text-slate-500 mt-1 truncate">Manquant : {d.missing_steps.join(", ")}</div>
                  )}
                </div>
                <div className="text-right shrink-0 w-28">
                  <div className={`text-lg font-bold ${d.completion_pct >= 100 ? "text-green-400" : "text-[#f7c935]"}`}>{d.completion_pct}%</div>
                  <div className="w-full bg-white/5 rounded-full h-1.5 mt-1">
                    <div className={`h-1.5 rounded-full ${d.completion_pct >= 100 ? "bg-green-400" : "bg-[#f7c935]"}`} style={{ width: `${d.completion_pct}%` }} />
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{d.done_count}/{d.total_steps} étapes</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {dossierEnrId && <EnrollmentDossier enrollmentId={dossierEnrId} api={api} onClose={() => setDossierEnrId(null)} onChanged={load} />}
    </div>
  );
}

